'use client'

import { Avatar } from '@repo/ui/Avatar'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useEffect, useMemo, useState } from 'react'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import MUIButton from '@mui/material/Button'
import MUIFormControl from '@mui/material/FormControl'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUISelect from '@mui/material/Select'

type NormalizedUser = { id: string; name: string; initials: string }

const menuItemStyles = {
  borderRadius: '4px',
  cursor: 'pointer',
  margin: '6px 6px',
  minHeight: '38px',
  minWidth: 'calc(264px - 38px)',
  pb: 0,
  pl: '8px',
  pt: 0,
  width: '100%'
}

export const ThirdPartyFormsCreatedByFilter = ({
  testId = 'created-by-filter'
}: {
  testId?: string
}) => {
  const {
    thirdPartyFormsTableFilters,
    setThirdPartyFormsTableFilters,
    thirdPartyFormsTableFilterCount
  } = useDataGridFilterStore()

  const { fetchAllUsers, allUsers } = useLookupsStore()

  const filters = thirdPartyFormsTableFilters?.filters ?? {}

  useEffect(() => {
    if (!allUsers || (Array.isArray(allUsers) && allUsers.length === 0)) {
      fetchAllUsers()
    }
  }, [allUsers, fetchAllUsers])

  const normalizedUsers = useMemo<NormalizedUser[]>(() => {
    const arr = Array.isArray(allUsers) ? allUsers : []
    return arr
      .map((u: any) => {
        const id = u?.userId ?? u?.bundleUserId ?? u?.id ?? u?.feeEarnerId ?? null
        const name =
          u?.description ?? u?.name ?? u?.fullName ?? u?.displayName ?? u?.email ?? String(id ?? '')
        const initials =
          u?.initials ??
          (typeof name === 'string'
            ? name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((p: string) => p[0]?.toUpperCase())
                .join('')
            : '')
        if (!id) return null
        return { id: String(id), name: String(name), initials: String(initials || '') }
      })
      .filter(Boolean) as NormalizedUser[]
  }, [allUsers])

  const idToName = useMemo(() => new Map(normalizedUsers.map((u) => [u.id, u.name])), [normalizedUsers])

  const [selectedId, setSelectedId] = useState<string>('')

  useEffect(() => {
    const stored = filters?.createdBy as string | null | undefined
    const next = stored ? String(stored) : ''
    setSelectedId((prev) => (prev === next ? prev : next))
  }, [filters?.createdBy])

  useEffect(() => {
    if (thirdPartyFormsTableFilterCount === 0) setSelectedId('')
  }, [thirdPartyFormsTableFilterCount])

  const handleChange = (event: any) => {
    const id = event.target.value as string
    setSelectedId(id)
    setThirdPartyFormsTableFilters({ createdBy: id || null })
  }

  const handleClear = () => {
    setSelectedId('')
    setThirdPartyFormsTableFilters({ createdBy: null })
  }

  const renderValue = () => {
    if (!selectedId) return 'Select'
    return idToName.get(selectedId) ?? selectedId
  }

  return (
    <>
      <MUIFormControl
        sx={{
          width: '100%',
          [`& > label`]: {
            backgroundColor: 'transparent',
            top: '-10px',
            transform: 'translate(8px, 15px) scale(1)'
          },
          [`& > label[data-shrink='true']`]: {
            backgroundColor: '#f5f6f8',
            top: 0,
            transform: 'translate(14px, -9px) scale(0.75)'
          }
        }}
      >
        <MUIInputLabel id="third-party-forms-created-by-filter-label">Select</MUIInputLabel>
        <MUISelect
          data-testid={testId}
          value={selectedId}
          onChange={handleChange}
          renderValue={renderValue}
          inputProps={{ 'data-testid': testId }}
          sx={{
            height: '32px',
            mb: 0,
            width: '100%',
            [`& .MuiSelect-select`]: {
              height: '18px !important',
              pt: '6px',
              pb: '6px',
              pl: '8px'
            },
            [`& .MuiSelect-nativeInput`]: {
              height: '32px !important',
              pt: 0,
              pb: 0,
              pl: '8px'
            },
            [`& .MuiSvgIcon-root`]: {
              right: 0
            }
          }}
        >
          {normalizedUsers
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((u) => (
              <MUIMenuItem key={u.id} value={u.id} sx={menuItemStyles}>
                <Avatar
                  initials={u.initials}
                  feeEarnerId={u.id as any}
                  name={u.name}
                  sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
                />
                {u.name}
              </MUIMenuItem>
            ))}
        </MUISelect>
      </MUIFormControl>
      <MUIButton
        onClick={handleClear}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          right: '32px',
          top: '24px',
          height: '0.6rem',
          width: '0.6rem',
          padding: 0,
          minWidth: 0
        }}
      >
        <CloseIcon sx={{ fill: 'black', height: '0.6rem', width: '0.6rem' }} />
      </MUIButton>
    </>
  )
}
